import React from 'react';
import { translate } from 'react-i18next';

import NotificationChoices from 'components/account/NotificationChoices.js';
import NotificationChannelCheckboxes from 'components/NotificationChannelCheckboxes.js';
import MultiFormatNotificationToggle from 'components/account/MultiFormatNotificationToggle.js';
import FancyCheckbox from 'components/account/FancyCheckbox.js';
import UIStatusDialog from 'components/UIStatusDialog.js';

// Multi-select additions (sms/email per event type, in addition to push)
// gated by multi_format_notifications_enabled. Rendered on this same page as
// the original single-select call-out preferences below (gated by
// advanced_callout_options) -- Product asked for one combined Notifications
// screen rather than two separate tabs, so a company/employee can see
// either section, or both at once, depending on which flag(s) are on.
//
// Each row also gets its own on/off toggle (MultiFormatNotificationToggle),
// matching mobile and the advanced-callout rows above -- without one, an
// employee who doesn't want a given event has no way to silence it short of
// disabling push for the whole app or marking sms/email as spam (Product,
// MS-5218). Manager rows are the one exception: not in this ticket's scope,
// and the backend has no matching on/off column for them, so they keep the
// plain checkboxes-only rendering below.
//
// One canonical order, matching mobile's own list (MS-5218 QA #28) --
// multiFormatTypes below is built by filtering this down to whichever types
// currently apply, never by concatenating sub-lists, so EW's order can't
// drift from mobile's as more conditional types get added.
const EMPLOYEE_NOTIFICATION_TYPES_ORDER = [
  'shift_offers',
  'offer_accepted',
  'offer_declined',
  'exchange_bids',
  'exchange_available',
  'drop_available',
  'exchange_bid_accepted',
  'exchange_bid_declined',
  'exchange_approved',
  'exchange_declined',
  'schedule_changed',
  'company_message',
  'meal_break_waiver',
];

// Backend only ever sends these to User.admin_users_responsible_for(...)
// (app/models/user.rb) -- never to a plain employee -- so there's no point
// showing them to one; a plain `employee` role would never receive them.
const MANAGER_NOTIFICATION_TYPES = [
  'manager_approval',
  'manager_exchange',
  'manager_exception',
  'manager_late',
  'manager_punch',
  'manager_clock',
];

// Explicit allowlist rather than "!== employee": if `role` is ever
// missing/unexpected (e.g. a backend payload regression), this fails closed
// (hides manager content) instead of showing it to everyone.
const MANAGER_ROLES = ['department_admin', 'location_admin', 'company_admin', 'super_admin'];

// Turning allow_exchanges_and_drops off shuts down the whole exchange/drop
// lifecycle -- nothing can be posted, offered, accepted, declined or
// approved, and existing ones are cleared -- so none of these seven events
// can fire; hide all of them (Product, MS-5218).
const DROP_AND_EXCHANGE_NOTIFICATION_TYPES = [
  'exchange_available',
  'drop_available',
  'exchange_approved',
  'exchange_declined',
  'exchange_bids',
  'exchange_bid_accepted',
  'exchange_bid_declined',
];

// Of those seven, only these two actually no-op server-side (not even push
// fires) when the narrower drop_and_exchange_notifications Flipper flag is
// off (NotificationService#notify_exchange_available!) -- the other five
// have no such guard and keep firing once the lifecycle itself is reachable,
// so that flag alone must not hide them (Product, MS-5218: "When only the
// exchange/drop notification flag is off... events 3-7 still fire").
const DROP_AND_EXCHANGE_AVAILABILITY_TYPES = ['exchange_available', 'drop_available'];

// Push is deliberately not offered as a choice here -- it's mandatory and
// always fires for every one of these event types regardless of this screen,
// so the only real decisions an employee makes are whether to ALSO get sms
// and/or email (both can be picked at once -- see NotificationChannelCheckboxes).
const MULTI_FORMAT_VALID_OPTIONS = ['sms', 'email'];

// When advanced call-outs are off, Product wants these 3 events to keep
// showing up -- just folded into the multi-format list (checkboxes, no
// phone option, no per-type on/off toggle) instead of disappearing, since
// the events themselves still happen. Reuses the existing call-out labels
// (there's no equivalent key under MultiFormatNotificationPreferences.types
// for these three).
const CALLOUT_NOTIFICATION_TYPES = ['shift_offers', 'offer_accepted', 'offer_declined'];
const CALLOUT_AS_MULTI_FORMAT_LABEL_KEYS = {
  shift_offers: 'AccountCalloutPreferences.new_shift_available',
  offer_accepted: 'AccountCalloutPreferences.shift_requested_is_assigned',
  offer_declined: 'AccountCalloutPreferences.shift_requested_is_declined',
};

class AccountCalloutPreferences extends React.Component {
  constructor(props) {
    super(props);
    this.handlePreferenceSave = this.handlePreferenceSave.bind(this);
    this.handleSettingChange = this.handleSettingChange.bind(this);
    this.handleChannelChange = this.handleChannelChange.bind(this);
    this.toggleSetting = this.toggleSetting.bind(this);
    this.toggleShiftOffers = this.toggleShiftOffers.bind(this);
    this.state = {
      settings: {},
      // Every on/off boolean (shift_offers, offer_accepted, ...) the employee
      // has actually flipped this visit, keyed by setting name. toggleSetting
      // only ever touched `settings` (local display state) and
      // handlePreferenceSave only ever sent `settings.preferences` (the
      // `_methods` arrays) -- so no on/off toggle on this screen was ever
      // actually saved; this is what the save payload below reads from now.
      toggledSettings: {},
      validationWarnings: '',
    };
  }

  componentWillReceiveProps(nextProps) {
    // Guard against unrelated parent re-renders (e.g. AccountView pushing a
    // save-success status dialog) stomping locally-edited, unsaved state
    // with whatever settings object the parent currently holds.
    if (nextProps.settings !== this.props.settings) {
      this.setState({
        settings: nextProps.settings,
        toggledSettings: {},
        user_id: nextProps.user.id,
      });
    }
  }

  handlePreferenceSave(e) {
    e.preventDefault();

    MakeShiftEmployeeDispatcher.dispatch({
      actionType: appConstants.ActionTypes.UPDATE_SETTINGS,
      postData: {
        ...this.state.settings.preferences,
        ...this.state.toggledSettings,
      },
    });
  }

  // Current on/off value for a boolean setting (e.g. "shift_offers"),
  // preferring whatever the employee has locally toggled this visit over
  // what the parent last loaded.
  getEnabled(name) {
    if (!_.isUndefined(this.state.settings[name])) {
      return this.state.settings[name];
    }
    return this.props.settings[name];
  }

  // MS-5218, Product (after the follow-up meeting): these two rows are
  // independently checkable while shift_offers is on -- just with no sms/
  // email picker of their own (they always use shift_offers' own channels,
  // enforced backend-side) -- and only exist at all while shift_offers
  // itself is on, matching mobile (nested under "A new shift becomes
  // available", hidden when that's off).
  renderConflictToggleRows() {
    const rows = [];
    if (!this.getEnabled('shift_offers')) {
      return rows;
    }

    // Order matches mobile: time off conflict first, then existing-shift conflict.
    if (this.props.allowShiftsWithTimeOffConflict) {
      rows.push(
        <div className="notificationOptionCluster conflictingShift" key="shift_offers_conflict_time_off">
          <FancyCheckbox
            disabled={false}
            checked={this.getEnabled('shift_offers_conflict_time_off')}
            label={this.props.t('AccountCalloutPreferences.shift_offers_conflict_time_off')}
            setting="shift_offers_conflict_time_off"
            toggleSetting={this.toggleSetting}
          />
        </div>,
      );
    }
    if (this.props.allowShiftsWithScheduledShiftConflict) {
      rows.push(
        <div className="notificationOptionCluster conflictingShift" key="shift_offers_conflict_shift">
          <FancyCheckbox
            disabled={false}
            checked={this.getEnabled('shift_offers_conflict_shift')}
            label={this.props.t('AccountCalloutPreferences.shift_offers_conflict_shift')}
            setting="shift_offers_conflict_shift"
            toggleSetting={this.toggleSetting}
          />
        </div>,
      );
    }
    return rows;
  }

  toggleSetting(setting, value) {
    const settings = _.clone(this.state.settings);
    const key = settings.hasOwnProperty(setting);
    if (key) {
      settings[setting] = value;
    }
    this.setState({
      settings,
      toggledSettings: { ...this.state.toggledSettings, [setting]: value },
    });
  }

  // shift_offers' own toggle, wrapping #toggleSetting: turning it off also
  // resets the two conflict rows to false, matching the backend
  // (UserSetting#sync_conflict_settings_with_shift_offers) and Product's
  // explicit rule -- they always start unchecked again the next time
  // shift_offers is turned back on, never remembering what they were before.
  // One setState call, not three sequential #toggleSetting calls, since each
  // of those reads/clones the not-yet-flushed this.state -- three in a row
  // here would have the last call's clone silently discard the first two.
  toggleShiftOffers(setting, value) {
    const settings = _.clone(this.state.settings);
    settings.shift_offers = value;
    const toggledSettings = { ...this.state.toggledSettings, shift_offers: value };

    if (!value) {
      settings.shift_offers_conflict_shift = false;
      settings.shift_offers_conflict_time_off = false;
      toggledSettings.shift_offers_conflict_shift = false;
      toggledSettings.shift_offers_conflict_time_off = false;
    }

    this.setState({ settings, toggledSettings });
  }

  // Single-select (call-out types: push/sms/email/phone, exactly one at a time).
  handleSettingChange(setting, value) {
    const settings = _.clone(this.state.settings);
    const key = settings.preferences.hasOwnProperty(setting);
    if (key) {
      settings.preferences[setting] = [value];
    }
    this.setState({
      settings,
    });
  }

  // Multi-select (sms/email per event type, any combination). Receives the
  // already-suffixed settingName (e.g. "schedule_changed_methods") --
  // NotificationChannelCheckboxes forwards its own `name` prop back here as
  // the first callback argument, and `name` is set to settingName below, not
  // the bare notificationType.
  handleChannelChange(settingName, channel) {
    const settings = _.clone(this.state.settings);
    const preferences = _.clone(settings.preferences || {});

    if (preferences.hasOwnProperty(settingName)) {
      const current = preferences[settingName] || [];
      const index = current.indexOf(channel);
      preferences[settingName] = index === -1
        ? current.concat(channel)
        : current.filter(selected => selected !== channel);
    }

    settings.preferences = preferences;
    this.setState({ settings });
  }

  render() {
    const { settings } = this.props;

    const shiftOfferNotifications = this.getEnabled('shift_offers');
    const offersAccetedNotifications = this.getEnabled('offer_accepted');
    const offersDeclinedNotifications = this.getEnabled('offer_declined');

    const statusDialogs = [];
    for (let i = 0; i < this.props.statuses.length; i++) {
      statusDialogs.push(
        <UIStatusDialog
          status={this.props.statuses[i]}
          key={this.props.statuses[i].key}
          closeDialog={this.props.closeStatusDialog}
        />,
      );
    }

    const validOptions = ['push', 'sms', 'email', 'phone'];

    if (!this.props.settings.preferences) {
      return false;
    }

    const isManager = this.props.user && MANAGER_ROLES.includes(this.props.user.role);
    let multiFormatTypes = EMPLOYEE_NOTIFICATION_TYPES_ORDER.filter((notificationType) => {
      if (CALLOUT_NOTIFICATION_TYPES.includes(notificationType)) {
        return !this.props.advancedCalloutOptions;
      }
      if (DROP_AND_EXCHANGE_NOTIFICATION_TYPES.includes(notificationType)) {
        if (!this.props.allowExchangesAndDrops) {
          return false;
        }
        if (DROP_AND_EXCHANGE_AVAILABILITY_TYPES.includes(notificationType)) {
          return this.props.dropAndExchangeNotificationsEnabled;
        }
        return true;
      }
      return true;
    });
    if (isManager) {
      multiFormatTypes = multiFormatTypes.concat(MANAGER_NOTIFICATION_TYPES);
    }

    // Company-level lock (CompanySetting#locked_notification_channels): when
    // present, it overrides every employee's own choice at delivery time
    // regardless of what's checked here -- so the UI reflects that reality
    // instead of showing a choice that doesn't actually apply. Locked to
    // push only still comes through as ['push'], not [], so length > 0 is a
    // safe "is locked" check here.
    const lockedChannels = this.props.settings.locked_notification_channels || [];
    const isLocked = lockedChannels.length > 0;

    return (
      <div className={this.props.activeState}>
        <form
          className="account-callout-preferences"
          onSubmit={this.handlePreferenceSave.bind(this)}
        >
          <fieldset className="account-preferences">
            <legend>
              <h1>{this.props.t('AccountCalloutPreferences.notification_preferences')}</h1>
            </legend>

            {this.props.advancedCalloutOptions && (
              <div className="callout-notification-preferences">
                <p>{this.props.t('AccountCalloutPreferences.receive_notifications_when')}:</p>
                <NotificationChoices
                  label={this.props.t('AccountCalloutPreferences.new_shift_available')}
                  contactInfoCallbackHandler={this.props.onClickHandler}
                  notificationType="shift_offers"
                  validOptions={validOptions}
                  user={this.props.user}
                  currentPreference={this.props.settings.preferences.shift_offers_methods}
                  settings={settings}
                  enabled={shiftOfferNotifications}
                  toggleSetting={this.toggleShiftOffers}
                  handleSettingChange={this.handleSettingChange.bind(this)}
                />
                {this.renderConflictToggleRows()}
                <NotificationChoices
                  label={this.props.t('AccountCalloutPreferences.shift_requested_is_assigned')}
                  contactInfoCallbackHandler={this.props.onClickHandler}
                  notificationType="offer_accepted"
                  validOptions={validOptions}
                  user={this.props.user}
                  currentPreference={this.props.settings.preferences.offer_accepted_methods}
                  settings={settings}
                  enabled={offersAccetedNotifications}
                  toggleSetting={this.toggleSetting.bind(this)}
                  handleSettingChange={this.handleSettingChange.bind(this)}
                />
                <NotificationChoices
                  label={this.props.t('AccountCalloutPreferences.shift_requested_is_declined')}
                  contactInfoCallbackHandler={this.props.onClickHandler}
                  notificationType="offer_declined"
                  validOptions={validOptions}
                  user={this.props.user}
                  currentPreference={this.props.settings.preferences.offer_declined_methods}
                  settings={settings}
                  enabled={offersDeclinedNotifications}
                  toggleSetting={this.toggleSetting.bind(this)}
                  handleSettingChange={this.handleSettingChange.bind(this)}
                />
              </div>
            )}

            {this.props.multiFormatNotificationsEnabled && (
              <div className="multi-format-notification-preferences">
                <p className="notification-preferences-intro">{this.props.t('MultiFormatNotificationPreferences.intro')}</p>
                {isLocked && (
                  <p className="notification-preferences-locked-notice">
                    {this.props.t('MultiFormatNotificationPreferences.locked_by_organization')}
                  </p>
                )}
                {multiFormatTypes.map((notificationType) => {
                  const settingName = `${notificationType}_methods`;
                  const currentPreference = this.state.settings.preferences
                    ? this.state.settings.preferences[settingName]
                    : settings.preferences[settingName];
                  const labelKey = CALLOUT_AS_MULTI_FORMAT_LABEL_KEYS[notificationType]
                    || `MultiFormatNotificationPreferences.types.${notificationType}`;

                  // Manager rows: out of scope for the toggle (no matching
                  // on/off column on the backend) -- keep the old
                  // checkboxes-only rendering.
                  if (MANAGER_NOTIFICATION_TYPES.includes(notificationType)) {
                    return (
                      <div className="notificationOptionCluster" key={notificationType}>
                        <p className="notification-type-title">
                          {this.props.t(labelKey)}
                        </p>
                        <div className="notificationOptions">
                          <NotificationChannelCheckboxes
                            name={settingName}
                            user={this.props.user}
                            validOptions={MULTI_FORMAT_VALID_OPTIONS}
                            currentPreference={currentPreference}
                            lockedChannels={lockedChannels}
                            onSettingsChange={this.handleChannelChange}
                            contactInfoCallbackHandler={this.props.onClickHandler}
                            activePage={this.props.activePage}
                          />
                        </div>
                      </div>
                    );
                  }

                  const isShiftOffers = notificationType === 'shift_offers';

                  // React 15 has no Fragment -- return the toggle plus (for
                  // shift_offers only) its conflict rows as a plain array of
                  // keyed siblings instead.
                  return [
                    <MultiFormatNotificationToggle
                      key={notificationType}
                      label={this.props.t(labelKey)}
                      notificationType={notificationType}
                      settingName={settingName}
                      enabled={this.getEnabled(notificationType)}
                      toggleSetting={isShiftOffers ? this.toggleShiftOffers : this.toggleSetting}
                      user={this.props.user}
                      validOptions={MULTI_FORMAT_VALID_OPTIONS}
                      currentPreference={currentPreference}
                      lockedChannels={lockedChannels}
                      onSettingsChange={this.handleChannelChange}
                      contactInfoCallbackHandler={this.props.onClickHandler}
                      activePage={this.props.activePage}
                    />,
                    ...(isShiftOffers ? this.renderConflictToggleRows() : []),
                  ];
                })}
              </div>
            )}

            <input
              name="login"
              type="submit"
              value={this.props.t('AccountCalloutPreferences.save_changes')}
            />
          </fieldset>
          {statusDialogs}
        </form>
      </div>
    );
  }
}

export default translate()(AccountCalloutPreferences);
